
import React, { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { MessageSquare, Search, Phone } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';

interface SocialBadgesProps {
  id?: string;
  type: 'telegram' | 'avito' | 'vk' | 'whatsapp';
  phone?: string | null;
}

// Базовый URL для всех сервисов Wazzup24
const WazzupBaseUrl = 'https://app.wazzup24.com/9783-0106';

// Добавляем идентификаторы каналов
const channelIds = {
  telegram: '941b5203-89b5-4ae4-90b9-7b0fee7786da',
  avito: 'c147dcd7-8bc0-4526-b327-1431d3c189ea',
  vk: '2253efd4-f072-4cb2-a4ad-536696bf9e52',
  whatsapp: 'f3821df8-003c-4a42-9e94-c51987941964'
};

export const SocialBadge: React.FC<SocialBadgesProps> = ({ id, type, phone }) => {
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  
  if (!id && !phone) return null;
  
  const colors = {
    telegram: 'bg-blue-500 hover:bg-blue-600',
    avito: 'bg-green-500 hover:bg-green-600',
    vk: 'bg-indigo-500 hover:bg-indigo-600',
    whatsapp: 'bg-emerald-500 hover:bg-emerald-600'
  };
  
  const icons = {
    telegram: <MessageSquare className="h-3 w-3 mr-1" />,
    avito: <Search className="h-3 w-3 mr-1" />,
    vk: <MessageSquare className="h-3 w-3 mr-1" />,
    whatsapp: <Phone className="h-3 w-3 mr-1" />
  };
  
  const labels = {
    telegram: 'Телеграм',
    avito: 'Авито',
    vk: 'ВКонтакте',
    whatsapp: 'WhatsApp'
  };

  // Обновляем формирование URL для конкретных диалогов
  const getChatUrl = () => {
    if (type === 'telegram' && id) {
      return `${WazzupBaseUrl}/chat/telegram/${id}/${channelIds.telegram}`;
    }
    if (type === 'avito' && id) {
      return `${WazzupBaseUrl}/chat/avito/${id}/${channelIds.avito}`;
    }
    if (type === 'vk' && id) {
      return `${WazzupBaseUrl}/chat/vk/${id}/${channelIds.vk}`;
    }
    if (type === 'whatsapp' && phone) {
      const cleanPhone = phone.replace(/\D/g, '');
      return `${WazzupBaseUrl}/chat/whatsapp/${cleanPhone}/${channelIds.whatsapp}`;
    }
    return '';
  };

  return (
    <>
      <Badge 
        className={`${colors[type]} text-white flex items-center text-xs cursor-pointer`}
        onClick={() => setIsSheetOpen(true)}
      >
        {icons[type]}
        {type === 'telegram' ? id : labels[type]}
      </Badge>

      <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
        <SheetContent side="right" className="w-full sm:w-[600px] p-0">
          <SheetHeader className="p-4 border-b">
            <SheetTitle>Чат {labels[type]}</SheetTitle>
          </SheetHeader>
          <iframe
            src={getChatUrl()}
            className="w-full h-[calc(100vh-80px)]"
            style={{ border: 'none' }}
          />
        </SheetContent>
      </Sheet>
    </>
  );
};

interface CourierSocialBadgesProps {
  telegram_id?: string;
  avito_id?: string;
  vk_id?: string;
  phone?: string | null;
}

export const CourierSocialBadges: React.FC<CourierSocialBadgesProps> = ({
  telegram_id,
  avito_id,
  vk_id,
  phone
}) => {
  // Всегда показывать WhatsApp, если есть номер телефона
  const hasPhone = !!phone;

  return (
    <div className="flex flex-wrap gap-1">
      {telegram_id && <SocialBadge id={telegram_id} type="telegram" />}
      {avito_id && <SocialBadge id={avito_id} type="avito" />}
      {vk_id && <SocialBadge id={vk_id} type="vk" />}
      {hasPhone && <SocialBadge type="whatsapp" phone={phone} />}
    </div>
  );
};
